const ALL_PAGE_KEYS = [
  'overview', 'dashboard', 'strategic-alignment', 'portfolios',
  'requests', 'projects', 'financials', 'capacity', 'okrs',
  'roadmaps', 'raid', 'templates', 'timesheets', 'team', 'beacon', 'settings',
] as const;

interface ResolvedPermissions {
  pages: Record<string, boolean>;
  canEdit: Record<string, boolean>;
}

/**
 * Resolve effective permissions for a user.
 * Priority: isOrgAdmin (full access) > role permissions > user-level overrides.
 */
export function resolvePermissions(dbUser: any): ResolvedPermissions {
  if (!dbUser) return { pages: {}, canEdit: {} };

  if (dbUser.isOrgAdmin) {
    const all: Record<string, boolean> = {};
    for (const k of ALL_PAGE_KEYS) all[k] = true;
    return { pages: { ...all }, canEdit: { ...all } };
  }

  const rolePerms = dbUser.tenantRole?.permissions;
  const resolved: ResolvedPermissions = {
    pages: { ...(rolePerms?.pages || {}) },
    canEdit: { ...(rolePerms?.canEdit || {}) },
  };

  // Apply per-user overrides (supports both legacy ViewMode strings and new booleans)
  const userPerms = dbUser.permissions;
  if (userPerms?.pages) {
    for (const [page, value] of Object.entries(userPerms.pages)) {
      resolved.pages[page] = !!value;
    }
  }
  if (userPerms?.canEdit) {
    for (const [page, value] of Object.entries(userPerms.canEdit)) {
      resolved.canEdit[page] = !!value;
    }
  }

  return resolved;
}

export function isAdminUser(dbUser: any): boolean {
  return !!dbUser?.isOrgAdmin;
}

export function hasPageAccess(dbUser: any, page: string): boolean {
  return resolvePermissions(dbUser).pages[page] === true;
}

export function canEditPage(dbUser: any, page: string): boolean {
  return resolvePermissions(dbUser).canEdit[page] === true;
}

export function getViewMode(dbUser: any, page: string): 'single' | 'team' | 'global' | null {
  if (!hasPageAccess(dbUser, page)) return null;
  return 'global'; // Tiered viewMode deferred to later
}

// Legacy stubs for backward compat
export function getAdGroups(_user: any): string[] {
  return [];
}

export function getCognitoGroups(_user: any): string[] {
  return [];
}
